import React from 'react';
import { modalStyles } from '../../config/formStyles';
import { useMediaQuery } from '../../hooks/useMediaQuery';

// En el celular el modal ocupa casi toda la pantalla y la botonera baja de
// línea si no entra, para que ningún botón quede fuera de alcance.
const mobileOverrides = {
    overlay: {
        padding: 'calc(env(safe-area-inset-top) + 0.5rem) 0.5rem calc(env(safe-area-inset-bottom) + 0.5rem)',
    },
    content: { width: '100%', maxHeight: '100%', padding: '1rem' },
    title: { fontSize: '1.25rem' },
    actions: { flexWrap: 'wrap', gap: '0.5rem' },
};

/**
 * Modal reutilizable de Axon Capital.
 * Replica el patrón del modal de Operaciones: overlay oscuro, contenedor flex
 * en columna con título fijo arriba, área scrolleable en el medio y footer de
 * botones fijo abajo.
 *
 * Props:
 * - isOpen: boolean — si es falso no renderiza nada.
 * - onClose: fn — usada por el footer/botón Cancelar del consumidor.
 * - title: node — título fijo.
 * - maxWidth: string — ancho máximo del contenedor (default '800px').
 * - onSubmit: fn opcional — si se pasa, el cuerpo se envuelve en <form> para
 *   permitir botones type="submit" (Enter envía el formulario).
 * - error: string opcional — banner de error arriba del contenido.
 * - footer: node opcional — botonera fija al pie.
 * - children: node — cuerpo scrolleable.
 * - zIndex: number — z-index del overlay (default 2000; usar 2001 si se apila).
 *
 * Comportamiento (igual que el modal de Operaciones): no cierra por click en el
 * overlay ni con Escape, para evitar pérdida accidental de datos en formularios.
 */
const Modal = ({
    isOpen,
    title,
    maxWidth = '800px',
    onSubmit,
    error,
    footer,
    children,
    zIndex = 2000,
}) => {
    const isMobile = useMediaQuery();
    if (!isOpen) return null;

    const m = isMobile ? mobileOverrides : {};

    const Body = onSubmit ? 'form' : 'div';
    const bodyProps = onSubmit
        ? { onSubmit, style: modalStyles.formColumn }
        : { style: modalStyles.formColumn };

    return (
        <div style={{ ...modalStyles.overlay, ...m.overlay, zIndex }}>
            <div style={{ ...modalStyles.content, maxWidth, ...m.content }}>
                {title && <h2 style={{ ...modalStyles.title, ...m.title }}>{title}</h2>}
                <Body {...bodyProps}>
                    <div style={modalStyles.scrollArea}>
                        {error && <p style={modalStyles.errorBanner}>{error}</p>}
                        {children}
                    </div>
                    {footer && <div className="axon-modal-actions" style={{ ...modalStyles.actions, ...m.actions }}>{footer}</div>}
                </Body>
            </div>
        </div>
    );
};

export default Modal;
